/**
 * 登录重定向工具
 * 功能：用户访问需要登录的页面时，登录成功后自动跳转回原页面
 */

/**
 * 跳转登录页（在需要登录的页面调用）
 * @param {string} loginUrl - 登录页地址，默认 '/login'
 */
export function goToLogin(loginUrl = '/login') {
  const currentUrl = window.location.href
  const urlObj = new URL(currentUrl)

  // 如果当前已经在登录页（无论是否带有 redirect 参数），不应该再嵌套重定向
  if (urlObj.pathname === loginUrl) {
    console.log('[AuthRedirect] 已经在登录页，忽略嵌套重定向')
    return
  }

  const encodedUrl = encodeURIComponent(currentUrl)
  const targetUrl = `${loginUrl}?redirect=${encodedUrl}`

  console.log('[AuthRedirect] goToLogin called:', {
    currentUrl,
    encodedUrl,
    targetUrl,
    loginUrl
  })

  window.location.href = targetUrl
}

/**
 * 获取登录后应跳转的地址（在登录页调用）
 * @returns {string|null} 重定向地址，如果没有则返回 null
 */
export function getRedirectUrl() {
  const urlParams = new URLSearchParams(window.location.search)
  const redirect = urlParams.get('redirect')

  console.log('[AuthRedirect] getRedirectUrl called:', {
    search: window.location.search,
    redirect
  })

  if (!redirect) {
    console.log('[AuthRedirect] No redirect param found')
    return null
  }

  try {
    // 解码并验证 URL 安全性
    const decodedUrl = decodeURIComponent(redirect)
    console.log('[AuthRedirect] Decoded URL:', decodedUrl)

    // 安全检查：只允许同域名的跳转，防止开放重定向漏洞
    const currentHost = window.location.host
    const redirectUrl = new URL(decodedUrl)

    console.log('[AuthRedirect] Host check:', {
      currentHost,
      redirectHost: redirectUrl.host
    })

    if (redirectUrl.host !== currentHost) {
      console.warn('[AuthRedirect] 拒绝跨域重定向:', redirectUrl.host)
      return null
    }

    console.log('[AuthRedirect] Valid redirect URL:', decodedUrl)
    return decodedUrl
  } catch (e) {
    console.error('[AuthRedirect] 重定向 URL 解析失败:', e)
    return null
  }
}

/**
 * 获取重定向路径（用于 React Router 的 navigate）
 * @returns {string} 重定向路径，如果没有则返回 '/'
 */
export function getRedirectPath() {
  const redirectUrl = getRedirectUrl()
  if (!redirectUrl) return '/'

  try {
    const url = new URL(redirectUrl)
    const path = url.pathname + url.search + url.hash
    console.log('[AuthRedirect] Redirect path:', path)
    
    // 如果重定向路径本身就是登录页（可能是因为之前错误的嵌套），则回退到首页
    if (url.pathname === '/login' || url.pathname === '/admin/login') {
      console.warn('[AuthRedirect] 拒绝重定向到登录页本身，回退到首页')
      return '/'
    }

    return path
  } catch (e) {
    console.error('[AuthRedirect] Failed to get redirect path:', e)
    return '/'
  }
}

/**
 * 登录成功后跳转（在登录页调用）
 * @param {Function} navigate - React Router 的 navigate 函数
 * @param {string} defaultPath - 默认跳转路径，如果没有重定向参数
 */
export function redirectAfterLogin(navigate, defaultPath = '/') {
  console.log('[AuthRedirect] redirectAfterLogin called:', {
    defaultPath,
    currentSearch: window.location.search
  })

  let redirectPath = getRedirectPath()
  
  // 如果 getRedirectPath 返回的是 '/' 并且 defaultPath 不是 '/'，则使用 defaultPath
  if (redirectPath === '/' && defaultPath !== '/') {
    redirectPath = defaultPath
  }

  console.log('[AuthRedirect] Will navigate to:', redirectPath)

  navigate(redirectPath)
}
